import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert,
  CircularProgress, Stack, Chip,
} from '@mui/material';
import {
  Save, Edit, Person, Phone, Email, Language, Instagram, CalendarToday,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface CreatorProfileData {
  id: string;
  store_name: string;
  owner_name: string;
  bio: string | null;
  contact_email: string;
  contact_phone: string;
  joined_at: string;
  social_links: {
    instagram?: string;
    website?: string;
    tiktok?: string;
    youtube?: string;
  } | null;
}

export default function CreatorProfile() {
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<CreatorProfileData | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    contact_phone: '',
    contact_email: '',
    instagram: '',
    website: '',
    tiktok: '',
    youtube: '',
  });

  useEffect(() => { loadProfile(); }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('seller_profile').select('*').eq('user_id', user.id).single();
      if (data) {
        setProfile(data as CreatorProfileData);
        setFormData({
          bio: data.bio || '',
          contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || '',
          instagram: data.social_links?.instagram || '',
          website: data.social_links?.website || '',
          tiktok: data.social_links?.tiktok || '',
          youtube: data.social_links?.youtube || '',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const socialLinks: any = {};
      if (formData.instagram) socialLinks.instagram = formData.instagram;
      if (formData.website) socialLinks.website = formData.website;
      if (formData.tiktok) socialLinks.tiktok = formData.tiktok;
      if (formData.youtube) socialLinks.youtube = formData.youtube;

      const { error } = await supabase.from('seller_profile').update({
        bio: formData.bio,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        social_links: socialLinks,
      }).eq('id', profile.id);

      if (error) throw error;
      setEditMode(false);
      loadProfile();
    } catch (e: any) {
      alert('저장에 실패했습니다: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        contact_phone: profile.contact_phone || '',
        contact_email: profile.contact_email || '',
        instagram: profile.social_links?.instagram || '',
        website: profile.social_links?.website || '',
        tiktok: profile.social_links?.tiktok || '',
        youtube: profile.social_links?.youtube || '',
      });
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (!profile) return <Alert severity="error">프로필을 찾을 수 없습니다.</Alert>;

  return (
    <Box sx={styles.pageContainer}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700} sx={styles.title}>
          크리에이터 프로필
        </Typography>
        {!editMode ? (
          <Button variant="contained" startIcon={<Edit />} onClick={() => setEditMode(true)}
            sx={{ bgcolor: '#111', '&:hover': { bgcolor: '#333' } }}>
            수정하기
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>취소</Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}
              sx={{ bgcolor: '#111', '&:hover': { bgcolor: '#333' } }}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* 기본 정보 (읽기 전용) */}
      <Paper elevation={0} sx={{ ...styles.paper, p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
          기본 정보
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Person color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">활동명</Typography>
                <Typography variant="body1" color={isDarkMode ? '#fff' : '#111'}>{profile.store_name}</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Person color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">성명</Typography>
                <Typography variant="body1" color={isDarkMode ? '#fff' : '#111'}>{profile.owner_name}</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">가입일</Typography>
                <Typography variant="body1" color={isDarkMode ? '#fff' : '#111'}>
                  {new Date(profile.joined_at).toLocaleDateString('ko-KR')}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">상태</Typography>
                <Box mt={0.5}>
                  <Chip label="크리에이터" size="small" sx={{ bgcolor: '#7c3aed20', color: '#7c3aed', fontWeight: 700 }} />
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* 자기소개 */}
      <Paper elevation={0} sx={{ ...styles.paper, p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
          자기소개
        </Typography>
        <TextField
          fullWidth multiline rows={4}
          label="자기소개 / 주요 카테고리"
          placeholder="패션, 뷰티, 라이프스타일 등 활동 분야를 소개해주세요."
          value={formData.bio}
          onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))}
          disabled={!editMode}
          sx={styles.input}
        />
      </Paper>

      {/* 연락처 */}
      <Paper elevation={0} sx={{ ...styles.paper, p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
          연락처
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="연락처"
              value={formData.contact_phone}
              onChange={e => setFormData(p => ({ ...p, contact_phone: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Phone sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="이메일" type="email"
              value={formData.contact_email}
              onChange={e => setFormData(p => ({ ...p, contact_email: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Email sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 소셜 링크 */}
      <Paper elevation={0} sx={{ ...styles.paper, p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
          소셜 링크
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Instagram"
              placeholder="https://instagram.com/username"
              value={formData.instagram}
              onChange={e => setFormData(p => ({ ...p, instagram: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Instagram sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Website"
              placeholder="https://example.com"
              value={formData.website}
              onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Language sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="TikTok"
              placeholder="https://tiktok.com/@username"
              value={formData.tiktok}
              onChange={e => setFormData(p => ({ ...p, tiktok: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Language sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="YouTube"
              placeholder="https://youtube.com/@username"
              value={formData.youtube}
              onChange={e => setFormData(p => ({ ...p, youtube: e.target.value }))}
              disabled={!editMode}
              InputProps={{ startAdornment: <Language sx={{ mr: 1 }} color="action" /> }}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
